import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { scoutService, ScoutQuery, ScoutQueryCreate } from '../../services/scout.service'
import ScoutQueryCard from '../../components/scout/ScoutQueryCard'
import ScoutQueryForm from '../../components/scout/ScoutQueryForm'
import CandidateCard from '../../components/scout/CandidateCard'

type CandidateStatus = 'pending' | 'approved' | 'dismissed' | 'blacklisted' | ''

export default function ScoutPage() {
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [editingQuery, setEditingQuery] = useState<ScoutQuery | null>(null)
  const [statusFilter, setStatusFilter] = useState<CandidateStatus>('pending')
  const [queryFilter, setQueryFilter] = useState<number | undefined>(undefined)
  const [minScore, setMinScore] = useState<number | undefined>(undefined)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())

  // ── Data fetching ──────────────────────────────────────────────
  const { data: queries = [], isLoading: loadingQueries } = useQuery({
    queryKey: ['scoutQueries'],
    queryFn: () => scoutService.getQueries(),
  })

  const { data: stats } = useQuery({
    queryKey: ['scoutStats'],
    queryFn: () => scoutService.getCandidateStats(),
  })

  const { data: candidates = [], isLoading: loadingCandidates } = useQuery({
    queryKey: ['scoutCandidates', statusFilter, queryFilter, minScore],
    queryFn: () =>
      scoutService.getCandidates({
        status: statusFilter || undefined,
        query_id: queryFilter,
        min_score: minScore,
      }),
  })

  // ── Mutations ──────────────────────────────────────────────────
  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['scoutQueries'] })
    queryClient.invalidateQueries({ queryKey: ['scoutCandidates'] })
    queryClient.invalidateQueries({ queryKey: ['scoutStats'] })
  }

  const createMutation = useMutation({
    mutationFn: (data: ScoutQueryCreate) => scoutService.createQuery(data),
    onSuccess: () => { invalidateAll(); setShowForm(false); toast.success('Запрос создан') },
    onError: () => toast.error('Ошибка создания запроса'),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: ScoutQueryCreate }) => scoutService.updateQuery(id, data),
    onSuccess: () => { invalidateAll(); setEditingQuery(null); setShowForm(false); toast.success('Запрос обновлён') },
    onError: () => toast.error('Ошибка обновления'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => scoutService.deleteQuery(id),
    onSuccess: () => { invalidateAll(); toast.success('Запрос удалён') },
    onError: () => toast.error('Ошибка удаления'),
  })

  const runNowMutation = useMutation({
    mutationFn: (id: number) => scoutService.runQueryNow(id),
    onSuccess: () => toast.success('Скаутинг запущен'),
    onError: () => toast.error('Ошибка запуска'),
  })

  const toggleMutation = useMutation({
    mutationFn: ({ id, active }: { id: number; active: boolean }) => scoutService.updateQuery(id, { is_active: active }),
    onSuccess: () => invalidateAll(),
  })

  const approveMutation = useMutation({
    mutationFn: (id: number) => scoutService.approveCandidate(id),
    onSuccess: () => { invalidateAll(); toast.success('Канал добавлен в мониторинг') },
    onError: () => toast.error('Ошибка'),
  })

  const dismissMutation = useMutation({
    mutationFn: (id: number) => scoutService.dismissCandidate(id),
    onSuccess: () => invalidateAll(),
  })

  const blacklistMutation = useMutation({
    mutationFn: (id: number) => scoutService.blacklistCandidate(id),
    onSuccess: () => { invalidateAll(); toast.success('Канал в blacklist') },
  })

  const bulkMutation = useMutation({
    mutationFn: ({ ids, action }: { ids: number[]; action: string }) => scoutService.bulkAction(ids, action),
    onSuccess: (data) => { invalidateAll(); setSelectedIds(new Set()); toast.success(`Обработано: ${data.processed}`) },
  })

  // ── Handlers ───────────────────────────────────────────────────
  const handleSubmit = (data: ScoutQueryCreate) => {
    if (editingQuery) {
      updateMutation.mutate({ id: editingQuery.id, data })
    } else {
      createMutation.mutate(data)
    }
  }

  const handleEdit = (q: ScoutQuery) => {
    setEditingQuery(q)
    setShowForm(true)
  }

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const statusTabs: { label: string; value: CandidateStatus; count?: number }[] = [
    { label: 'На ревью', value: 'pending', count: stats?.pending },
    { label: 'Добавлены', value: 'approved', count: stats?.approved },
    { label: 'Пропущены', value: 'dismissed', count: stats?.dismissed },
    { label: 'Blacklist', value: 'blacklisted', count: stats?.blacklisted },
    { label: 'Все', value: '', count: stats?.total },
  ]

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Channel Scout</h1>
          <p className="text-sm text-gray-500 mt-1">
            Автоматический поиск и AI-оценка каналов
          </p>
        </div>
        <button
          onClick={() => { setEditingQuery(null); setShowForm(true) }}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          + Новый запрос
        </button>
      </div>

      {/* Query form */}
      {showForm && (
        <ScoutQueryForm
          editingQuery={editingQuery}
          onSubmit={handleSubmit}
          onCancel={() => { setShowForm(false); setEditingQuery(null) }}
        />
      )}

      {/* Queries */}
      {loadingQueries ? (
        <div className="text-gray-500">Загрузка...</div>
      ) : queries.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2">
          {queries.map(q => (
            <ScoutQueryCard
              key={q.id}
              query={q}
              onEdit={handleEdit}
              onDelete={id => deleteMutation.mutate(id)}
              onRunNow={id => runNowMutation.mutate(id)}
              onToggle={(id, active) => toggleMutation.mutate({ id, active })}
            />
          ))}
        </div>
      ) : !showForm ? (
        <div className="text-center py-10 text-gray-500">
          Нет поисковых запросов. Создайте первый для автоматического скаутинга.
        </div>
      ) : null}

      {/* Candidates section */}
      <div className="border-t pt-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">Найденные каналы</h2>
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Выбрано: {selectedIds.size}</span>
              <button
                onClick={() => bulkMutation.mutate({ ids: Array.from(selectedIds), action: 'approve' })}
                className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700"
              >
                Добавить все
              </button>
              <button
                onClick={() => bulkMutation.mutate({ ids: Array.from(selectedIds), action: 'dismiss' })}
                className="px-3 py-1.5 border border-gray-300 text-sm rounded-lg hover:bg-gray-50"
              >
                Пропустить все
              </button>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4 mb-4 flex-wrap">
          <div className="flex gap-1">
            {statusTabs.map(tab => (
              <button
                key={tab.value}
                onClick={() => setStatusFilter(tab.value)}
                className={`px-3 py-1.5 text-sm rounded-lg ${
                  statusFilter === tab.value
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {tab.label}
                {tab.count != null && tab.count > 0 && (
                  <span className="ml-1 opacity-75">({tab.count})</span>
                )}
              </button>
            ))}
          </div>

          {queries.length > 1 && (
            <select
              value={queryFilter ?? ''}
              onChange={e => setQueryFilter(e.target.value ? Number(e.target.value) : undefined)}
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
            >
              <option value="">Все запросы</option>
              {queries.map(q => (
                <option key={q.id} value={q.id}>{q.query_text}</option>
              ))}
            </select>
          )}

          <select
            value={minScore ?? ''}
            onChange={e => setMinScore(e.target.value ? Number(e.target.value) : undefined)}
            className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
          >
            <option value="">Любой score</option>
            <option value={80}>80+</option>
            <option value={60}>60+</option>
            <option value={40}>40+</option>
          </select>
        </div>

        {/* Candidate list */}
        {loadingCandidates ? (
          <div className="text-gray-500">Загрузка...</div>
        ) : candidates.length > 0 ? (
          <div className="space-y-3">
            {candidates.map(c => (
              <CandidateCard
                key={c.id}
                candidate={c}
                onApprove={id => approveMutation.mutate(id)}
                onDismiss={id => dismissMutation.mutate(id)}
                onBlacklist={id => blacklistMutation.mutate(id)}
                selected={selectedIds.has(c.id)}
                onSelect={statusFilter === 'pending' ? toggleSelect : undefined}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-10 text-gray-400">
            Нет кандидатов с текущими фильтрами
          </div>
        )}
      </div>
    </div>
  )
}
